const SECTION_TYPE_ORDER = [
  "foundation",
  "structure",
  "wall",
  "cladding",
  "roof",
  "ceiling",
  "flooring",
  "painting",
  "windows_doors",
  "custom",
  "unknown",
];

function humanize(value = "") {
  return String(value)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatSourceTypeLabel(sourceType = "") {
  const map = {
    foundation: "Foundation",
    structure: "Structure",
    wall: "Walls",
    cladding: "Cladding",
    roof: "Roof",
    ceiling: "Ceiling",
    flooring: "Flooring",
    painting: "Painting",
    windows_doors: "Windows / Doors",
    custom: "Custom",
    unknown: "Other",
  };

  return map[sourceType] || humanize(sourceType) || "Other";
}

function formatSectionTitle(sourceType = "", sourceLabel = "", sourceId = "") {
  const raw = String(sourceLabel || sourceId || "").trim().toLowerCase();

  if (!raw) return formatSourceTypeLabel(sourceType);

  if (raw.includes("short_side_cladding")) return "Cladding — short side";
  if (raw.includes("long_side_cladding_low"))
    return "Cladding — long side (low)";
  if (raw.includes("long_side_cladding_high"))
    return "Cladding — long side (high)";
  if (raw.includes("roof"))
    return sourceType === "roof" ? humanize(sourceLabel) : "Roof";
  if (raw.includes("foundation")) return "Foundation";
  if (raw.includes("structure")) return "Structure";

  return humanize(sourceLabel) || formatSourceTypeLabel(sourceType);
}

function getSectionSortIndex(sourceType = "", title = "") {
  const normalizedType = String(sourceType || "").toLowerCase();
  const normalizedTitle = String(title || "").toLowerCase();

  if (normalizedTitle.includes("foundation")) return 0;
  if (normalizedTitle.includes("structure")) return 1;
  if (normalizedTitle.includes("cladding")) return 2;
  if (normalizedTitle.includes("roof")) return 3;
  if (normalizedTitle.includes("ceiling")) return 4;
  if (normalizedTitle.includes("floor")) return 5;
  if (normalizedTitle.includes("paint")) return 6;
  if (normalizedTitle.includes("window") || normalizedTitle.includes("door")) {
    return 7;
  }

  const typeIndex = SECTION_TYPE_ORDER.indexOf(normalizedType);
  return typeIndex >= 0 ? typeIndex : SECTION_TYPE_ORDER.length;
}

function isInactive(row = {}) {
  return Boolean(
    row.inactive ||
      row.excluded ||
      row?.flags?.inactive ||
      row?.flags?.excluded ||
      row?.meta?.inactive ||
      row?.meta?.excluded
  );
}

function getRawRowTotal(row = {}) {
  if (row?.total !== undefined && row?.total !== null) {
    return Number(row.total) || 0;
  }

  const quantity = Number(row?.quantity ?? row?.qty ?? row?.hours ?? 0) || 0;
  const unitPrice =
    Number(row?.unitPrice ?? row?.price ?? row?.hourlyRate ?? row?.rate ?? 0) ||
    0;

  return quantity * unitPrice;
}

function getRowTotal(row = {}) {
  if (isInactive(row)) return 0;
  return getRawRowTotal(row);
}

export function groupEstimateBySection(estimate = {}) {
  const materials = Array.isArray(estimate?.materials) ? estimate.materials : [];
  const labour = Array.isArray(estimate?.labour) ? estimate.labour : [];
  const extras = Array.isArray(estimate?.extras) ? estimate.extras : [];

  const allRows = [...materials, ...labour, ...extras];
  const grouped = new Map();

  for (const row of allRows) {
    const sourceType = row?.meta?.sourceType || "unknown";
    const sourceId = row?.meta?.sourceId || `${sourceType}_default`;
    const sourceLabel = row?.meta?.sourceLabel || sourceType;
    const key = `${sourceType}__${sourceId}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        id: key,
        key,
        title: formatSectionTitle(sourceType, sourceLabel, sourceId),
        sourceType,
        sourceId,
        sourceLabel,
        rows: [],
      });
    }

    grouped.get(key).rows.push(row);
  }

  return Array.from(grouped.values())
    .map((section) => {
      const inactiveRows = section.rows.filter(isInactive);
      const activeRows = section.rows.filter((row) => !isInactive(row));

      const activeTotal = activeRows.reduce(
        (sum, row) => sum + getRawRowTotal(row),
        0
      );

      const inactiveTotal = inactiveRows.reduce(
        (sum, row) => sum + getRawRowTotal(row),
        0
      );

      const rawTotal = section.rows.reduce(
        (sum, row) => sum + getRawRowTotal(row),
        0
      );

      return {
        ...section,

        total: activeTotal,
        rawTotal,
        inactiveTotal,

        rowCount: section.rows.length,
        activeRowCount: activeRows.length,
        inactiveRowCount: inactiveRows.length,

        activeRows,
        inactiveRows,

        // Backwards compatibility
        excludedRows: inactiveRows,
        excludedTotal: inactiveTotal,
        excludedRowCount: inactiveRows.length,
      };
    })
    .sort((a, b) => {
      const orderDiff =
        getSectionSortIndex(a.sourceType, a.title) -
        getSectionSortIndex(b.sourceType, b.title);

      if (orderDiff !== 0) return orderDiff;

      return a.title.localeCompare(b.title);
    });
}